import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Sun,
  Moon,
  Circle,
  CheckCircle2,
  Clock,
  Flame,
  Sprout,
  Map as MapIcon,
  ChevronRight,
  Sunrise,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { cn } from "../../lib/utils";
import { scrollReveal } from "./motionConstants";
import { queryClient } from "../../lib/queryClient";
import useRetryFetch from "../../hooks/useRetryFetch";
import {
  listTakeaways,
  toggleStatus,
  editTakeaway,
  UserTakeaway,
} from "../../api/userTakeaways";
import {
  listPwfs,
  getQuestSummary,
  addPwfLog,
  togglePwfStatus,
  updatePwf,
  QuestSummary,
} from "../../api/pwf";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Skeleton } from "../ui/skeleton";
import { splitTodayTasks } from "../../lib/todayTasks";
import { trackHomeEvent } from "../../lib/homeAnalytics";
import { dueLabelOf, dueDateText } from "../../lib/dueDates";
import { useSpaces } from "../../context/SpaceProvider";
import { useProjectIndex } from "../../hooks/useProjectIndex";
import { ProjectChip } from "../portal/myWorkspace/ProjectChip";

// ─── Date helpers ────────────────────────────────────────────────────────────

const endOfToday = () => {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d.getTime();
};

/** Local midnight N days from now — same convention ActionItemDialog stores */
export const dueInDays = (days: number) => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + days);
  return d.getTime();
};

/** Same date key JourneyWalk/JourneyMap stamp on habit_check logs */
const todayKey = () => new Date().toISOString().slice(0, 10);

// ─── Data shapes ─────────────────────────────────────────────────────────────

interface JourneyToday {
  journeyId: string;
  title: string;
  /** Habits from every phase in motion — a learner can walk several at once */
  habits: { name: string; checked: boolean; phaseId: string }[];
  streak: number;
}

// ─── Component ───────────────────────────────────────────────────────────────

const MAX_VISIBLE_TASKS = 3;
const MAX_JOURNEYS = 3;

interface TodayCardProps {
  /** "slim" = Today's lower half (spec 2026-11-04 §2.5): no card chrome, "N of M", workspace link. */
  variant?: "card" | "slim";
  /** Takeaway ids already shown as For now — never repeated in the top three. */
  excludeIds?: string[];
}

const TodayCard = ({ variant = "card", excludeIds = [] }: TodayCardProps = {}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const retryFetch = useRetryFetch();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [restingOpen, setRestingOpen] = useState(false);
  const { spaces } = useSpaces();
  const { projectById } = useProjectIndex();

  // Pending action items — split into "today" (due or carried) and "resting"
  const { data: actionsData, isLoading: actionsLoading } = useQuery({
    queryKey: ["home-today-actions"],
    queryFn: () =>
      listTakeaways(retryFetch, { type: "action", status: "pending", limit: 200 }),
    staleTime: 1000 * 30,
  });

  // Active journeys → quest summaries → today's habits for the current phase
  const { data: journeys, isLoading: journeysLoading } = useQuery<JourneyToday[]>({
    queryKey: ["home-today-journeys"],
    queryFn: async () => {
      const pwfs = await listPwfs(retryFetch);
      // Journey roots often stay "draft" while their phases progress, so
      // filter out only the clearly finished/parked ones
      const roots = pwfs
        .filter(
          (p) =>
            p.pwfType === "pwf" &&
            !p.isTemplate &&
            !p.pwfMeta?.fiParent &&
            !["completed", "archived", "paused"].includes(p.lifecycleState)
        )
        .slice(0, MAX_JOURNEYS);
      const summaries = await Promise.allSettled(
        roots.map((r) => getQuestSummary(retryFetch, r._id))
      );
      const result: JourneyToday[] = [];
      summaries.forEach((s, i) => {
        if (s.status !== "fulfilled") return;
        const q: QuestSummary = s.value;
        // Every phase in motion contributes habits — first open phase plus any
        // the learner explicitly set out on (parallel walking).
        const firstOpen = q.phases.findIndex((p) => p.state !== "completed");
        const engaged = q.phases.filter(
          (p, idx) =>
            p.state !== "completed" &&
            (idx === firstOpen || ["active", "in_progress"].includes(p.state))
        );
        if (engaged.length === 0) return;
        const checked = new Set(q.todayChecks);
        const habits = engaged
          .flatMap((p) =>
            p.habits.map((h) => ({
              name: h.name,
              checked: checked.has(h.name),
              phaseId: p.id,
            }))
          )
          .slice(0, 5);
        result.push({
          journeyId: roots[i]._id,
          title: q.journey.title,
          habits,
          streak: q.streak.current,
        });
      });
      return result;
    },
    staleTime: 1000 * 60,
  });

  const { todayTasks, restingTasks } = useMemo(() => {
    // Synthetic __isPwf rows (journey steps) carry parentId = their PHASE's
    // pwf id, which would otherwise drop them from the top-level split. Only
    // promote steps that actually carry a due date — undated steps from
    // later, not-yet-engaged phases should stay off both lists rather than
    // flood "resting".
    const items = (actionsData?.list ?? []).map((it) =>
      it.__isPwf && it.dueDate != null ? ({ ...it, parentId: undefined } as UserTakeaway) : it
    );
    const { today, resting } = splitTodayTasks(items, endOfToday());
    return { todayTasks: today, restingTasks: resting };
  }, [actionsData]);
  const restingCount = restingTasks.length;

  // ── Actions ────────────────────────────────────────────────────────────────

  const completeTask = async (item: UserTakeaway) => {
    if (busyId) return;
    setBusyId(item._id);
    // Optimistic: the felt win first
    queryClient.setQueryData(
      ["home-today-actions"],
      (old: typeof actionsData) =>
        old ? { ...old, list: old.list.filter((it) => it._id !== item._id) } : old
    );
    try {
      if (item.__isPwf) {
        await togglePwfStatus(retryFetch, item._id);
        queryClient.invalidateQueries({ queryKey: ["quest-summary"] });
        queryClient.invalidateQueries({ queryKey: ["home-today-journeys"] });
      } else {
        await toggleStatus(retryFetch, { id: item._id });
      }
      trackHomeEvent("today_task_completed", { taskId: item._id });
      toast.success(t("home_today_done_toast"));
    } catch {
      toast.error(t("action_save_error", "Something went wrong"));
    } finally {
      setBusyId(null);
      queryClient.invalidateQueries({ queryKey: ["home-today-actions"] });
    }
  };

  const deferTask = async (item: UserTakeaway, days: number, whenLabel: string) => {
    if (busyId) return;
    setBusyId(item._id);
    queryClient.setQueryData(
      ["home-today-actions"],
      (old: typeof actionsData) =>
        old
          ? {
              ...old,
              list: old.list.map((it) =>
                it._id === item._id ? { ...it, dueDate: dueInDays(days) } : it
              ),
            }
          : old
    );
    try {
      if (item.__isPwf) {
        await updatePwf(retryFetch, item._id, {
          config: { dueDate: new Date(dueInDays(days)).toISOString() },
        });
        queryClient.invalidateQueries({ queryKey: ["quest-summary"] });
      } else {
        await editTakeaway(retryFetch, { id: item._id, dueDate: dueInDays(days) });
      }
      toast.success(t("home_today_deferred_toast", { when: whenLabel }));
    } catch {
      toast.error(t("action_save_error", "Something went wrong"));
    } finally {
      setBusyId(null);
      queryClient.invalidateQueries({ queryKey: ["home-today-actions"] });
    }
  };

  const checkHabit = async (j: JourneyToday, habitName: string, phaseId: string) => {
    // Optimistic on both this card and the journey map's cache
    queryClient.setQueryData<JourneyToday[]>(["home-today-journeys"], (old) =>
      old?.map((row) =>
        row.journeyId === j.journeyId
          ? {
              ...row,
              habits: row.habits.map((h) =>
                h.name === habitName ? { ...h, checked: true } : h
              ),
            }
          : row
      )
    );
    try {
      await addPwfLog(retryFetch, phaseId, `Habit practiced: ${habitName}`, {
        event: "habit_check",
        meta: { habit: habitName, date: todayKey() },
      });
      toast.success(t("home_today_habit_toast"));
    } catch (e: any) {
      toast.error(e?.message ?? t("action_save_error", "Something went wrong"));
    } finally {
      queryClient.invalidateQueries({ queryKey: ["home-today-journeys"] });
      queryClient.invalidateQueries({ queryKey: ["quest-summary", j.journeyId] });
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  const isLoading = actionsLoading || journeysLoading;
  const hasHabits = (journeys ?? []).some((j) => j.habits.length > 0);
  const nowMs = Date.now();
  const slim = variant === "slim";
  const listedTasks = excludeIds.length ? todayTasks.filter((it) => !excludeIds.includes(it._id)) : todayTasks;
  const visibleTasks = listedTasks.slice(0, MAX_VISIBLE_TASKS);
  const hiddenCount = listedTasks.length - visibleTasks.length;

  const dateLabel = new Date().toLocaleDateString(i18n.language, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  return (
    <motion.section
      {...scrollReveal}
      aria-labelledby="home-today-title"
      className={
        slim
          ? "relative"
          : cn(
              "relative rounded-2xl border border-primary/15 overflow-hidden",
              "bg-gradient-to-br from-card via-card to-primary/[0.03]",
              "shadow-sm shadow-primary/[0.03]"
            )
      }
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-4 pb-2">
        <div className="flex items-center gap-2">
          <Sun className="w-4 h-4 text-amber-500" />
          <h2 id="home-today-title" className="text-sm font-semibold text-foreground tracking-tight">
            {t("home_today_title")}
          </h2>
          {!slim && <span className="text-xs text-muted-foreground/60">{dateLabel}</span>}
        </div>
        {slim && listedTasks.length > 0 && (
          <span className="text-xs text-muted-foreground">
            {t("todayHome.today.shownOf", { shown: visibleTasks.length, total: listedTasks.length })}
          </span>
        )}
        {!slim && Math.max(...(journeys ?? []).map((j) => j.streak), 0) > 0 && (
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <Flame className="w-3.5 h-3.5 text-orange-500" />
            {t("day_streak", {
              count: Math.max(...(journeys ?? []).map((j) => j.streak)),
            })}
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="px-5 pb-5 space-y-2.5">
          <Skeleton className="h-9 w-full rounded-xl bg-gray-100" />
          <Skeleton className="h-9 w-3/4 rounded-xl bg-gray-100" />
        </div>
      ) : (
        <>
          {/* Tasks for today */}
          {visibleTasks.length > 0 ? (
            <ul className="px-3 pb-1">
              {visibleTasks.map((item) => {
                  const dueLabel = item.dueDate != null ? dueLabelOf(Number(item.dueDate), nowMs) : null;
                  const isCarried =
                    dueLabel?.kind === "carried_weekday" || dueLabel?.kind === "carried_since";
                  return (
                    <motion.li
                      key={item._id}
                      layout
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.18 }}
                      className="group flex items-start gap-2.5 px-2 py-2 rounded-xl hover:bg-primary/[0.03] transition-colors"
                    >
                      <button
                        onClick={() => completeTask(item)}
                        aria-label={t("complete")}
                        className="shrink-0 mt-0.5 text-muted-foreground/40 hover:text-primary transition-colors"
                      >
                        <Circle className="w-[18px] h-[18px] group-hover:hidden" />
                        <CheckCircle2 className="w-[18px] h-[18px] hidden group-hover:block" />
                      </button>
                      <span className="flex-1 min-w-0 text-sm text-foreground/90 line-clamp-2 break-words">
                        {item.content}
                      </span>
                      {item.__isPwf && item.source?.engagementName && (
                        <span
                          className="shrink-0 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-primary/5 text-primary/80 border border-primary/15 max-w-[140px]"
                          title={t("home_today_same_place")}
                        >
                          <MapIcon className="w-2.5 h-2.5 flex-none" />
                          <span className="truncate">{item.source.engagementName}</span>
                        </span>
                      )}
                      {item.source?.type === "reflection" && item.source.question && (
                        <span
                          className="shrink-0 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-primary/5 text-primary/80 border border-primary/15 max-w-[140px]"
                          title={item.source.question}
                        >
                          <Sunrise className="w-2.5 h-2.5 flex-none" />
                          <span className="truncate">{item.source.question}</span>
                        </span>
                      )}
                      <ProjectChip
                        item={item}
                        projectById={projectById}
                        spaces={spaces}
                        className="shrink-0 text-[10px] px-1.5 py-0.5 border border-transparent"
                      />
                      {isCarried && dueLabel && (
                        <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100">
                          {dueLabel.kind === "carried_since"
                            ? t("home_today_carried_since", { date: dueDateText(dueLabel, i18n.language) })
                            : t("home_today_carried")}
                        </span>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            aria-label={t("home_today_later")}
                            title={t("home_today_later")}
                            className="shrink-0 inline-flex items-center justify-center min-w-[44px] min-h-[44px] -my-2.5 -me-2.5 text-muted-foreground/50 hover:text-muted-foreground rounded-lg opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                          >
                            <Clock className="w-3.5 h-3.5" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            className="cursor-pointer"
                            onClick={() =>
                              deferTask(item, 1, t("home_today_defer_tomorrow"))
                            }
                          >
                            {t("home_today_defer_tomorrow")}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="cursor-pointer"
                            onClick={() =>
                              deferTask(item, 7, t("home_today_defer_next_week"))
                            }
                          >
                            {t("home_today_defer_next_week")}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="cursor-pointer"
                            onClick={() =>
                              deferTask(item, 30, t("home_today_defer_next_month"))
                            }
                          >
                            {t("home_today_defer_next_month")}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </motion.li>
                  );
                })}
              {!slim && hiddenCount > 0 && (
                <li>
                  <button
                    onClick={() => navigate("/portal?tab=my_workspace")}
                    className="flex items-center gap-1 px-4 py-1.5 text-xs text-primary/80 hover:text-primary"
                  >
                    {t("home_today_more", { count: hiddenCount })}
                    <ChevronRight className="w-3 h-3 rtl:rotate-180" />
                  </button>
                </li>
              )}
            </ul>
          ) : (
            <p className="px-5 pb-3 text-sm text-muted-foreground/80 italic">
              {t("home_today_empty")}
            </p>
          )}

          {/* Today's practices from active journeys */}
          {hasHabits && (
            <div className="px-5 pt-2 pb-3 border-t border-primary/10">
              <p className="text-[11px] font-semibold text-muted-foreground/60 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                <Sprout className="w-3 h-3 text-emerald-600" />
                {t("home_today_habits")}
              </p>
              <div className="space-y-2">
                {(journeys ?? [])
                  .filter((j) => j.habits.length > 0)
                  .map((j) => (
                    <div key={j.journeyId} className="flex flex-wrap items-center gap-1.5">
                      <button
                        onClick={() => navigate(`/pwf?map=${j.journeyId}`)}
                        className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-primary me-1"
                        title={t("home_today_open_map")}
                      >
                        <MapIcon className="w-3 h-3" />
                        {j.title}
                      </button>
                      {j.habits.map((h) => (
                        <button
                          key={h.name}
                          disabled={h.checked}
                          onClick={() => checkHabit(j, h.name, h.phaseId)}
                          className={cn(
                            "inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border transition-all",
                            h.checked
                              ? "bg-emerald-50 border-emerald-200 text-emerald-700 cursor-default"
                              : "bg-white border-gray-200 text-foreground/70 hover:border-emerald-300 hover:text-emerald-700"
                          )}
                        >
                          {h.checked ? (
                            <CheckCircle2 className="w-3 h-3" />
                          ) : (
                            <Circle className="w-3 h-3 opacity-40" />
                          )}
                          {h.name}
                        </button>
                      ))}
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* The rest of life, safely parked */}
          {restingCount > 0 && (
            <div className="px-5 pb-3">
              <button
                onClick={() => setRestingOpen((o) => !o)}
                aria-expanded={restingOpen}
                className="w-full flex items-center gap-1.5 text-xs text-muted-foreground/70 hover:text-muted-foreground italic py-1.5 min-h-[44px] text-start"
              >
                <Moon className="w-3 h-3" />
                {t("home_today_resting", { count: restingCount })}
                <ChevronRight
                  className={cn("w-3 h-3 transition-transform rtl:rotate-180", restingOpen && "rotate-90 rtl:rotate-90")}
                />
              </button>
              {restingOpen && (
                <ul className="mt-1 space-y-1">
                  {restingTasks.slice(0, 5).map((it) => (
                    <li key={it._id} className="text-xs text-muted-foreground/60 line-clamp-1 ps-[18px]">
                      {it.content}
                    </li>
                  ))}
                  {restingTasks.length > 5 && (
                    <li>
                      <button
                        onClick={() => navigate("/portal?tab=my_workspace")}
                        className="text-xs text-primary/70 hover:text-primary ps-[18px]"
                      >
                        {t("home_today_more", { count: restingTasks.length - 5 })}
                      </button>
                    </li>
                  )}
                </ul>
              )}
            </div>
          )}
          {slim && (
            <div className="px-5 pb-2">
              <button
                onClick={() => navigate("/portal?tab=my_workspace")}
                className="inline-flex min-h-[44px] items-center gap-1 text-sm text-primary/80 hover:text-primary"
              >
                {t("todayHome.today.workspace")}
                <ChevronRight className="w-3.5 h-3.5 rtl:rotate-180" />
              </button>
            </div>
          )}
        </>
      )}
    </motion.section>
  );
};

export default TodayCard;
